/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import React, { useState } from "react";
import { useSmtpEmailForm } from "./hooks";
import { handleSmtpDataChecked, handleSmtpDataUnchecked } from "../../helpers";
import { isValidPassword } from "../../../../utils/validations";
import { Label } from "../../../ui/label";
import { Button } from "../../../ui/button";
import { Checkbox } from "../../../ui/checkbox";
import { RadioGroup, RadioGroupItem } from "../../../ui/radio-group";
import { InputField } from "../../../Shared/Radix/Inputs/InputField";
import { PasswordField } from "../../../Shared/Radix/Inputs/PasswordField";

const SmtpEmail: React.FC = () => {
  const {
    errMsg,
    setErrMsg,
    SmtpServerData,
    setSmtpServerData,
    onBlurHandler,
    handleResetTrapRecieve,
    submitHandler,
    enableSmtp,
    setEnableSmtp,
    smtpEmailDetails,
    selectedSecurity,
    setSelectedSecurity,
    submitTestEmailHandler
  } = useSmtpEmailForm();

  const [isEditMode, setIsEditMode] = useState(false);

  const onFieldChange = (name: string, value: string) => {
    if (name === "smtpPassword") {
      const isValidPass = isValidPassword(value);
      if (!isValidPass) {
        setErrMsg({ ...errMsg, [name]: `Password is invalid. It must meet the specified criteria!` });
      } else setErrMsg({ ...errMsg, [name]: "" });
    }
    setSmtpServerData((prev: any) => ({ ...prev, [name]: value }));
  };

  const checkboxHandler = (name: string, checked: boolean) => {
    if (name === "enableSmtp") {
      setEnableSmtp(checked);
      if (checked) {
        const newData: any = handleSmtpDataChecked(name, SmtpServerData, smtpEmailDetails);
        setSmtpServerData(newData);
      } else {
        setSelectedSecurity("none");
        const newData: any = handleSmtpDataUnchecked(name, SmtpServerData);
        setSmtpServerData(newData);
      }
    } else setSmtpServerData({ ...SmtpServerData, [name]: checked });
  };

  const handleCheckVal = (val: any) => {
    if (val === "true") return true;
    else if (val === "false") return false;
    else if (val === true) return true;
    else if (val === false) return false;
    else return false;
  };

  const handleEnterEditMode = () => {
    setIsEditMode(true);
  };

  const handleCancelEditMode = () => {
    handleResetTrapRecieve();
    setIsEditMode(false);
  };

  const handleSaveClick = async () => {
    const errCount = Object.values(errMsg).some((err) => err !== "");
    if (!errCount) {
      const success = await submitHandler();
      if (success) setIsEditMode(false);
    }
  };

  const getSecurityLabel = () => {
    if (selectedSecurity === "ssl") return "SSL";
    if (selectedSecurity === "startls") return "STARTTLS";
    return "None";
  };

  return (
    <div
      className="flex flex-col w-full px-4 md:px-4 pt-1 pb-4"
      style={{ color: "var(--foreground)" }}
    >
      <div className="rounded-md border border-border shadow-sm">
        {/* Header */}
        <div className="flex w-full items-start justify-between border-b border-border px-6 py-2">
          <div className="mb-3 flex flex-col items-start text-left">
            <h2 className="m-0 mt-4 text-sm font-semibold leading-tight text-foreground">SMTP</h2>
            <p className="m-0 mt-2 text-sm text-muted-foreground">Outbound mail server and authentication for notifications.</p>
          </div>
          <div className="mt-2 shrink-0">
            {!isEditMode ? (
              <Button type="button" onClick={handleEnterEditMode} className="bg-[#004f69] text-white hover:bg-[#004f69]/90">
                Edit
              </Button>
            ) : (
              <div className="flex items-center gap-2">
                <Button type="button" variant="outline" onClick={handleCancelEditMode}>
                  Cancel
                </Button>
                <Button type="button" onClick={handleSaveClick} className="bg-[#004f69] text-white hover:bg-[#004f69]/90">
                  Save
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Body */}
        <div className="p-6">
          {!isEditMode ? (
            /* ====== VIEW MODE ====== */
            <div className="space-y-4">
              {/* Enable */}
              <div className="flex flex-col gap-1">
                <span className="text-sm font-medium text-muted-foreground">Enable</span>
                <span className="text-base font-semibold text-foreground">{enableSmtp ? "Yes" : "No"}</span>
              </div>

              {/* From Email | Email Key */}
              <div className="grid grid-cols-1 gap-x-0 gap-y-3 md:grid-cols-[30%_70%]">
                <div className="flex flex-col gap-1">
                  <span className="text-sm font-medium text-muted-foreground">From Email</span>
                  <span className="text-base font-semibold text-foreground">{SmtpServerData?.fromEmail || "—"}</span>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-sm font-medium text-muted-foreground">Email Key</span>
                  <span className="text-base font-semibold text-foreground">{SmtpServerData?.emailKey || "—"}</span>
                </div>
              </div>

              {/* SMTP Server */}
              <div className="grid grid-cols-1 gap-x-0 gap-y-5 md:grid-cols-[30%_70%]">
                <div className="flex flex-col gap-1">
                  <span className="text-sm font-medium text-muted-foreground">SMTP Server</span>
                  <span className="text-base font-semibold text-foreground">{SmtpServerData?.smtpHost || "—"}</span>
                </div>
              </div>

              {/* SMTP Port | Security */}
              <div className="grid grid-cols-1 gap-x-0 gap-y-5 md:grid-cols-[30%_70%]">
                <div className="flex flex-col gap-1">
                  <span className="text-sm font-medium text-muted-foreground">SMTP Port</span>
                  <span className="text-base font-semibold text-foreground">{SmtpServerData?.smtpPort || "—"}</span>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-sm font-medium text-muted-foreground">Security</span>
                  <span className="text-base font-semibold text-foreground">{getSecurityLabel()}</span>
                </div>
              </div>

              {/* Use Authentication */}
              <div className="flex flex-col gap-1">
                <span className="text-sm font-medium text-muted-foreground">Use Authentication</span>
                <span className="text-base font-semibold text-foreground">{handleCheckVal(SmtpServerData?.useSmtpAuth) ? "Yes" : "No"}</span>
              </div>

              {/* Username | Password in bordered container */}
              <div className="rounded-md border border-border p-4 max-w-xl bg-muted">
                <div className="grid grid-cols-1 gap-x-0 gap-y-4 md:grid-cols-2">
                  <div className="flex flex-col gap-1">
                    <span className="text-sm font-medium text-muted-foreground">Username</span>
                    <span className="text-base font-semibold text-foreground">{SmtpServerData?.smtpUsername || "—"}</span>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-sm font-medium text-muted-foreground">Password</span>
                    <span className="text-base font-semibold text-foreground">{SmtpServerData?.smtpPassword ? "••••••••" : "—"}</span>
                  </div>
                </div>
              </div>

              {/* Send Test Email */}
              <div className="flex flex-col gap-1">
                <span className="text-sm font-medium text-muted-foreground">Send Test Email</span>
                <span className="text-base font-semibold text-foreground">{SmtpServerData?.testEmail || "—"}</span>
              </div>
            </div>
          ) : (
            /* ====== EDIT MODE ====== */
            <form autoComplete="off">
              <div className="space-y-8 max-w-xl">
                {/* Enable */}
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="enableSmtp"
                    checked={enableSmtp}
                    onCheckedChange={(checked) => checkboxHandler("enableSmtp", !!checked)}
                    className="m-0"
                  />
                  <Label htmlFor="enableSmtp" className="cursor-pointer">
                    Enable
                  </Label>
                </div>

                {/* From Email */}
                <div className="w-4/5">
                <InputField
                  label="From Email"
                  name="fromEmail"
                  disabled={!enableSmtp}
                  value={SmtpServerData?.fromEmail}
                  onChange={onFieldChange}
                  onBlur={onBlurHandler}
                  placeholder="Enter from email address"
                />
                </div>
                {/* Email Key */}
                <div className="w-3/5">
                <InputField
                  label="Email Key"
                  name="emailKey"
                  disabled={!enableSmtp}
                  value={SmtpServerData?.emailKey}
                  onChange={onFieldChange}
                  onBlur={onBlurHandler}
                  maxLength={2048}
                  tooltipText="Maximum length: 2048 characters"
                  placeholder="Enter email key"
                />
                </div>
                {/* SMTP Server */}
                <div className="w-4/5">
                <InputField
                  label="SMTP Server"
                  name="smtpHost"
                  disabled={!enableSmtp}
                  value={SmtpServerData?.smtpHost}
                  onChange={onFieldChange}
                  onBlur={onBlurHandler}
                  maxLength={2048}
                  placeholder="Enter SMTP server address"
                  tooltipText="Maximum length: 2048 characters"
                />
                </div>

                {/* SMTP Port */}
                <div className="w-1/6">
                <InputField
                  label="SMTP Port"
                  name="smtpPort"
                  type="number"
                  disabled={!enableSmtp}
                  value={SmtpServerData.smtpPort}
                  onChange={onFieldChange}
                  onBlur={onBlurHandler}
                  required
                  min={1}
                  max={65535}
                  placeholder="Enter SMTP port"
                  tooltipText="Value should be between 1 and 65535"
                />
                </div>

                {/* Security */}
                <div className="flex flex-col gap-4">
                  <Label className="text-sm font-medium">Security</Label>
                  <RadioGroup
                    value={selectedSecurity}
                    onValueChange={(value) => setSelectedSecurity(value)}
                    className="flex flex-row gap-6"
                    disabled={!enableSmtp}
                  >
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="none" id="security-none" />
                      <Label htmlFor="security-none" className="cursor-pointer font-normal">None</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="startls" id="security-starttls" />
                      <Label htmlFor="security-starttls" className="cursor-pointer font-normal">STARTTLS</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="ssl" id="security-ssl" />
                      <Label htmlFor="security-ssl" className="cursor-pointer font-normal">SSL</Label>
                    </div>
                  </RadioGroup>
                </div>

                {/* Authentication section */}
                <div className="flex items-center gap-2">
                    <Checkbox
                      id="useSmtpAuth"
                      disabled={!enableSmtp}
                      checked={handleCheckVal(SmtpServerData?.useSmtpAuth)}
                      onCheckedChange={(checked) => checkboxHandler("useSmtpAuth", !!checked)}
                      className="m-0"
                    />
                    <Label htmlFor="useSmtpAuth" className="cursor-pointer">
                      Use Authentication
                    </Label>
                  </div>
                <div className="rounded-md border border-border p-4 space-y-4">
                  {/* User Name */}
                  <div className="w-2/3">
                  <InputField
                    label="User Name"
                    name="smtpUsername"
                    disabled={!SmtpServerData?.useSmtpAuth}
                    value={SmtpServerData.smtpUsername}
                    onChange={onFieldChange}
                    onBlur={onBlurHandler}
                    maxLength={512}
                    tooltipText="Maximum length: 512 characters"
                    placeholder="Enter user name"
                  />
                  </div>

                  {/* Password */}
                  <div className="w-2/3">
                  <PasswordField
                    label="Password"
                    name="smtpPassword"
                    disabled={!SmtpServerData?.useSmtpAuth}
                    value={SmtpServerData?.smtpPassword}
                    onChange={onFieldChange}
                    onBlur={onBlurHandler}
                    placeholder="Enter password"
                  />
                  </div>
                </div>

                {/* Send Test Email */}
                <div className="flex flex-col gap-2">
                  <InputField
                    label="Send Test Email"
                    name="testEmail"
                    value={SmtpServerData.testEmail}
                    onChange={onFieldChange}
                    onBlur={onBlurHandler}
                    placeholder="Enter test email"
                  />
                  <Button
                    type="button"
                    disabled={!enableSmtp && errMsg?.testEmail?.length > 1 ? true : false}
                    onClick={submitTestEmailHandler}
                    className="h-10 w-fit"
                  >
                    Send
                  </Button>
                </div>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default SmtpEmail;
